import { useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import * as queryString from 'query-string'
import { useTranslation } from 'react-i18next'
import cx from 'classnames'
import { useParams, useLocation } from 'react-router'
import qs from 'query-string'

import { SessionStorage } from 'lib/storage'
import { Link, useNavigate } from 'features/i18n/router'
import { addNotification } from 'store/slices/notifications'
import { useBanWord } from 'hooks'
import { useCreateReviewMutation, useGetUserQuery } from 'store/api/yay/user'
import { snowplowEvent } from 'lib/analytics'
import { useCurrentUser } from 'store/api/yay/user'

import Avatar from '../Avatar'

const UserReviewModal = ({
  gift,
  birthday,
  returnPath,
}: {
  gift: any
  birthday: any
  returnPath: any
}) => {
  const dispatch = useDispatch()
  let { userId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  const { isContainBanWord, showDetectedBanWordAlert } = useBanWord()
  const [createReview] = useCreateReviewMutation()
  const { data: currentUser } = useCurrentUser()

  // This modal can be activated from `<ActivityUsersBirthdayModal />` and `<ActivitySendLetterBackButton />` as well,
  // in which case the modal will appear under activities page,
  // which doesn't load the target user by default. So we'll need to use
  // `useGetUserQuery` and get `userId` from query params.
  userId = userId ?? qs.parse(location.search)?.userId?.toString?.()

  const ___isComposing = useRef<boolean>() // `___isComposing` is when a user types in Japanese and selecting a proper kanji.
  const [text, setText] = useState('')

  const userRes = useGetUserQuery({
    userId: userId?.toString?.(),
  })

  const user = userRes?.data

  const onSubmit = () => {
    // If there is any character other than whitespaces or newlines.
    if (text.match(/\S+/g)) {
      if (isContainBanWord(text, 'review')) {
        showDetectedBanWordAlert()
        return
      }

      createReview({
        userId: user?.id,
        comment: text,
      }).then(async (res: any) => {
        if ((res?.data?.result ?? '') == 'success') {
          snowplowEvent.send_letter({
            letter: {
              sender_id: currentUser?.id,
              receiver_id: user?.id,
              is_sender_follow_receiver: user?.following,
              is_sender_follow_by_receiver: user?.followed_by,
            },
          })

          // logEvent.review_send({
          //   target_user_id: get(this.props, 'user?.id'),
          // })

          if (birthday) {
            SessionStorage.birthdayLetter.set({
              ...SessionStorage.birthdayLetter.get({}),
              [user?.id]: true,
            })
          }

          setText('')

          dispatch(
            addNotification({
              message: t('user_review_modal.success_notification'),
            })
          )

          navigate?.(
            queryString.parse(location.search)?.returnPath?.toString?.()
          )

          // Just reload rather than dealing with the pagination of the infinity loader.
          window.setTimeout(() => {
            window.location.reload()
          }, 400)
        }
      })
    }
  }

  return (
    <div className="Modal__wrapper Modal__wrapper--auto-h">
      <div className="Modal__header Modal__header--bdrz-t">
        <Link
          className={cx(
            'Modal__header__close',
            returnPath && 'Modal__header__close--back'
          )}
          to={returnPath || location.pathname}
        />

        <h2 className="Modal__header__h2">{t('common.write_letter')}</h2>
      </div>

      <div className="Modal__body">
        <div className="Letter">
          <textarea
            className="Letter__textarea"
            name="comment"
            onCompositionStart={() => {
              ___isComposing.current = true
            }}
            onCompositionEnd={() => {
              ___isComposing.current = false
            }}
            onKeyDown={(e) => {
              // Press Enter to send.
              // Press Enter + Shift to newline.
              if (
                e.key.toLowerCase() == 'enter' &&
                !e.shiftKey &&
                !___isComposing.current
              ) {
                e.preventDefault()
                onSubmit()
              }
            }}
            rows={5}
            placeholder={
              gift
                ? t('user_review_modal.input.gift_placeholder')
                : birthday
                ? t('user_review_modal.input.birthday_placeholder')
                : t('user_review_modal.input.placeholder', {
                    nickname: user?.nickname,
                  })
            }
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={200}
          />
          <span className="Letter__hint">{text.length ?? 0}/200</span>
        </div>
      </div>

      <div className="Modal__footer Modal__footer--minus-margin">
        <div className="Letter__footer">
          <div className="Letter__footer__to">
            <div className="Letter__footer__to__wrapper">
              <strong className="Letter__footer__to__strong">To:</strong>
              <Avatar user={user} />
              <span className="Letter__footer__to__span">{user?.nickname}</span>
            </div>
          </div>

          <button
            className="Button Button--green Button--icon-chat-send"
            onClick={onSubmit}
          >
            {t('common.send')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default UserReviewModal
